import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from 'utils/axiosInterceptors';
import { Alert, Badge, Form, Spinner } from 'react-bootstrap';
import { ColumnDef } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { TSalesInvoice, TStoreDay } from 'utils/types';
import StartDayPanel from './StartDayPanel';
import StartingCashBanner from './StartingCashBanner';
import { formatDateTime12hUtc8 } from 'utils/formats';
import { useBranchContext } from 'providers/BranchProvider';
import { useLocation } from 'react-router-dom';
import { addPaymentMethodIcon } from 'utils/addPaymentMethodIcon';
import BadgeStatus from 'utils/BadgeStatus';
import DataTable from '../BranchProductInventory/DataTable';

type SalesInvoiceFilters = {
  [key: string]: string | undefined;
  status: string | undefined;
  showTodayOnly: string | undefined;
};

const InvoicesTab = () => {
  const statusRef = useRef<HTMLSelectElement>(null);
  const { selectedBranch } = useBranchContext();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState<SalesInvoiceFilters>({
    status: queryParams.get('status') || undefined,
    showTodayOnly: queryParams.get('showTodayOnly') ?? 'true'
  });

  const storeDayQuery = useQuery({
    queryKey: ['storeDay', selectedBranch?.branchDomain],
    enabled: !!selectedBranch?.branchDomain,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TStoreDay>(
        `/api/v1/${selectedBranch?.branchDomain as string}/storeDays/today`
      );
      return data;
    }
  });

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    if (!selectedBranch?.branchDomain) {
      throw new Error('Branch domain not set');
    }

    const { data } = await axiosInstance.get(
      `/api/v1/${selectedBranch.branchDomain as string}/salesInvoices`,
      {
        params: {
          page,
          pageSize,
          search,
          orderBy,
          orderByDesc,
          ...filters
        }
      }
    );

    return data;
  };

  const handleFilterChange = (key: string, value: string | boolean) => {
    const newFilters = {
      ...filters,
      [key]: typeof value === 'boolean' ? String(value) : value
    };
    setFilters(newFilters);

    const newQueryParams = new URLSearchParams(location.search);
    Object.keys(newFilters).forEach(filterKey => {
      if (newFilters[filterKey]) {
        newQueryParams.set(filterKey, newFilters[filterKey] as string);
      } else {
        newQueryParams.delete(filterKey);
      }
    });
    navigate({ search: newQueryParams.toString() });
  };

  const columns: ColumnDef<TSalesInvoice>[] = [
    {
      header: 'Invoice Number',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { salesInvoiceId, invoiceNumber, status } = original;
        return (
          <Link
            to={
              status === 'Submitted'
                ? `/sales-details/${salesInvoiceId}`
                : `/edit-sale-transaction/${original.salesInvoiceId}`
            }
            className="text-decoration-none fw-bold"
          >
            {invoiceNumber}
          </Link>
        );
      }
    },
    {
      header: 'Sold To',
      accessorKey: 'soldTo',
      enableSorting: false
    },
    {
      header: 'Status',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { status, hasReturns, isForDeletion } = original;
        return (
          <div>
            <span>
              <BadgeStatus isForDeletion={isForDeletion} status={status} />
            </span>
            {hasReturns == true && (
              <span className="ms-2">
                <Badge bg="warning" className="fs-10">
                  Returns
                </Badge>
              </span>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'invoicedOn',
      header: 'Invoiced On',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const formattedDateTime =
          original.invoicedOn && formatDateTime12hUtc8(original.invoicedOn);
        return <span>{formattedDateTime}</span>;
      }
    },
    {
      accessorKey: 'invoiceAmount',
      enableSorting: false,
      header: 'Amount',
      cell: ({ row: { original } }) => {
        const { invoiceAmount, invoiceDiscountedAmount } = original;
        return (
          <span>
            {invoiceDiscountedAmount
              ? invoiceDiscountedAmount
              : invoiceAmount?.toLocaleString()}
          </span>
        );
      }
    },
    {
      header: 'Code',
      accessorKey: 'uniqueCode',
      enableSorting: false
    },
    {
      header: 'Method',
      accessorKey: 'paymentMethodId',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { paymentMethodId } = original;
        return <span>{addPaymentMethodIcon(paymentMethodId)}</span>;
      }
    },
    {
      header: 'Assisted By',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return (
          <span>
            {original.salesStaffFirstName} {original.salesStaffLastName}
          </span>
        );
      }
    },
    {
      header: 'Edit',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return original.status !== 'Submitted' ? (
          <Link to={`/edit-sale-transaction/${original.salesInvoiceId}`}>
            <FontAwesomeIcon icon={faEdit} />
          </Link>
        ) : null;
      }
    }
  ];

  if (selectedBranch?.branchDomain) {
    if (storeDayQuery.isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      );
    }

    if (storeDayQuery.isError) {
      return (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <span>Could not check whether the day has started.</span>
          <button
            type="button"
            className="btn btn-sm btn-phoenix-secondary"
            onClick={() => storeDayQuery.refetch()}
          >
            Retry
          </button>
        </Alert>
      );
    }

    if (storeDayQuery.data && !storeDayQuery.data.started) {
      return <StartDayPanel branchDomain={selectedBranch.branchDomain} />;
    }
  }

  const startingCash = storeDayQuery.data?.startingCash;

  return (
    <>
      {startingCash != null && selectedBranch?.branchDomain && (
        <StartingCashBanner
          branchDomain={selectedBranch.branchDomain}
          startingCash={startingCash}
        />
      )}
      <DataTable
        queryKey={['salesInvoice', filters]}
        queryFn={fetchData}
        columns={columns}
        rightSideActions={
        <>
          <Form.Check
            type="checkbox"
            id="defaultCheckbox"
            label="Today's transaction"
            checked={filters.showTodayOnly === 'true'}
            onChange={e => {
              handleFilterChange('showTodayOnly', e.target.checked);
            }}
          />

          <Form.Select
            className="w-25"
            value={filters.status}
            onChange={e => handleFilterChange('status', e.target.value)}
            ref={statusRef}
          >
            <option hidden> Filter By: Status </option>
            <option value=""> All </option>
            <option value="New"> New </option>
            <option value="InProgress"> In Progress </option>
            <option value="Submitted"> Submitted </option>
          </Form.Select>
        </>
        }
      />
    </>
  );
};

export default InvoicesTab;
